'use client';
import { useRouter } from 'next/router';
import { slugify } from '@moar/utils';
import { useCallback, useEffect, useState } from 'react';
import tippy, { hideAll } from 'tippy.js';

interface GenerateToolTipProps {
    term: string;
    def: string;
    url: string;
    isLight: boolean;
    backTo?: string;
}
// generate html for outside of react tooltip
const generateToolTip = (props: GenerateToolTipProps) => {
    const { term, def, url: urlBase, isLight, backTo } = props;
    const slugTerm = slugify(term);

    // the glossary page can't work out where it was opened from on its own (document.referrer is
    // not updated by client side navigation), so the page opening it says where to return to
    let url = urlBase;
    if (url && backTo) {
        url = `${urlBase}?back=${encodeURIComponent(backTo)}`;
    }

    return `
        <div class="tooltip infoPopup infoPopup--glossary ${isLight ? 'tooltip--light' : ''}">
            <p>${def}</p>
            ${
                url
                    ? `<a href="${url}#${slugTerm}" class="button glossary-button"><span>Glossary</span><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5.36764 1.97306L12.7404 0.0339112C13.481 -0.160883 14.1567 0.515015 13.9618 1.25559L12.0214 8.62681C11.8727 9.19149 11.0419 9.08688 11.0378 8.50298L11.0134 4.96817C11.0123 4.79537 10.9892 4.62777 10.9468 4.46803L1.70712 13.7081C1.31661 14.0987 0.683442 14.0987 0.292909 13.7082C-0.097624 13.3176 -0.0976381 12.6845 0.292878 12.2939L9.53557 3.05084C9.37357 3.00715 9.20341 2.98329 9.02787 2.98203L5.49123 2.9566C4.90736 2.95241 4.80296 2.12158 5.36764 1.97306Z" fill="#004899"></path></svg></a>`
                    : ''
            }
        </div>`;
};

export interface UseGlossaryProps {
    ref: any;
    url?: string;
    isLight?: boolean;
    rerenderTrigger?: any;
    // in app path the glossary should return to, e.g. `/sections#<object slug>` or `/objects/<slug>`
    backTo?: string;
}

export const useGlossary = (props: UseGlossaryProps) => {
    const { ref, url = '/glossary', isLight = false, rerenderTrigger, backTo } = props;
    const router = useRouter();

    // backTo, as the url of the page you are on right now - so it keeps params the tooltip was
    // rendered before, e.g. preview mode or a search. Undefined when backTo is some other page,
    // which is already a history entry of its own.
    const getBackToOnThisPage = useCallback(() => {
        if (!backTo) return undefined;

        const [path, hash] = backTo.split('#');
        if (path !== window.location.pathname) return undefined;

        return `${path}${window.location.search}${hash ? `#${hash}` : ''}`;
    }, [backTo]);

    // The hash of the page you are on is wherever you last jumped to (a section from the main nav,
    // say) - it does not follow you as you scroll. So before leaving for the glossary, point the
    // current history entry at the term's own object/section: that is the entry the browser back
    // button comes back to, and without this it takes you to the section you came in at.
    const alignHistoryWithBackTo = useCallback(
        async (target: string) => {
            if (target === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;

            await router.replace(target, undefined, { shallow: true, scroll: false }).catch(() => {
                // a failed replace only means back goes where it went before
            });
        },
        [router]
    );

    // tooltip has <a> for glossary with anchor to term, but that opens page with refresh
    // that was broken offline, so now we intercept click and push to router
    const handleClickGlossaryButton = useCallback(
        async (event: Event, instance: any) => {
            event.preventDefault();
            if (!event.target) return;

            const getAnchor = (eventTarget: EventTarget) => {
                if (eventTarget instanceof HTMLAnchorElement) return eventTarget;
                // span
                if (eventTarget instanceof HTMLElement) return eventTarget.closest('a');
                // svg
                if (eventTarget instanceof Node) return eventTarget.parentElement?.closest('a');
                return null;
            };
            const anchor = getAnchor(event.target);
            if (!anchor) return;

            // close tooltip
            if (instance) {
                instance.hide();
            }

            // read the href before the replace below changes the url it was built from
            const href = new URL(anchor.href);
            const backToOnThisPage = getBackToOnThisPage();

            if (backToOnThisPage) {
                // the tooltip was rendered before any search/filter, so take the current url
                href.searchParams.set('back', backToOnThisPage);
                await alignHistoryWithBackTo(backToOnThisPage);
            }

            router.push(`${href.pathname}${href.search}${href.hash}`).catch((e) => {
                // workaround for https://github.com/vercel/next.js/issues/37362
                if (!e.cancelled) {
                    throw e;
                }
            });
            return false;
        },
        [router, alignHistoryWithBackTo, getBackToOnThisPage]
    );

    const initTooltips = useCallback(() => {
        if (!ref.current) return;
        const textArea = ref.current;
        const wordRefs = [...textArea.querySelectorAll('.wordDefine')];
        if (!wordRefs.length) return;

        wordRefs.forEach((elem) => {
            if (elem._tippy) return;

            tippy(elem, {
                interactive: true,
                content: 'some text',
                allowHTML: true,
                arrow: false,
                placement: 'bottom',
                animation: 'scale-subtle',
                appendTo: document.body,
                // for debug
                // trigger: 'click',
                onShow(instance) {
                    hideAll({ exclude: instance });
                    // add listener to glossary button to route it through next instead of href so it works offline
                    const openTooltip = instance.popper.querySelector('.tippy-content');
                    const glossaryButton = openTooltip?.querySelector('.glossary-button');
                    glossaryButton?.addEventListener('click', (event) => handleClickGlossaryButton(event, instance), {
                        once: true,
                    });
                },
                onHide(instance) {
                    // remove listener for glossary button
                    const openTooltip = instance.popper.querySelector('.tippy-content');
                    const glossaryButton = openTooltip?.querySelector('.glossary-button');
                    glossaryButton?.removeEventListener('click', (event) => handleClickGlossaryButton(event, instance));
                },
                onCreate(instance) {
                    const content = generateToolTip({
                        term: elem.getAttribute('data-term'),
                        def: elem.getAttribute('data-def'),
                        url,
                        isLight,
                        backTo,
                    });
                    instance.setContent(content);
                },
            });
        });
    }, [isLight, ref, url, backTo, handleClickGlossaryButton]);

    useEffect(() => {
        initTooltips();
    }, [initTooltips, rerenderTrigger, router.pathname]);
};

export default useGlossary;
